import { concatMap, map, tap } from 'rxjs';
import { CrudServiceBase, RxUtils, TypeUtils } from '@utils';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { ProductCategoryType } from '@services/product';
import { QuotationItem } from './quotation-item.class';

export type QuotationItemsGrouped = Partial<Record<ProductCategoryType, QuotationItem[]>>;

export class QuotationItemService extends CrudServiceBase<QuotationItem> {
	constructor(quotationId: string) {
		super(QuotationItem, `quotation/${quotationId}/item`);
	}

	getItems() {
		return httpService.get<QuotationItem[]>(this.getUrl()).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getItems.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(QuotationItem, result))
		);
	}

	getItemsGrouped() {
		return this.getItems().pipe(map((items) => this.groupBy(items)));
	}

	getItemsForSite(siteId: string) {
		return httpService.get<QuotationItem[]>(this.getUrl(), `site/${siteId}`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getItemsForSite.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(QuotationItem, result))
		);
	}

	getItemsForSiteGrouped(siteId: string) {
		return this.getItemsForSite(siteId).pipe(map((items) => this.groupBy(items)));
	}

	getItemsForParkingArea(parkingAreaId: string) {
		return httpService.get<QuotationItem[]>(this.getUrl(), `park-area/${parkingAreaId}`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getItemsForParkingArea.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(QuotationItem, result))
		);
	}

	getItemsForParkingAreaGrouped(parkingAreaId: string) {
		return this.getItemsForParkingArea(parkingAreaId).pipe(map((items) => this.groupBy(items)));
	}

	private groupBy(quotationItems: QuotationItem[]): QuotationItemsGrouped {
		return (
			(quotationItems || []).reduce((acc, item) => {
				const type = item.product.category;
				!acc[type] && (acc[type] = []);
				acc[type]?.push(item);

				return acc;
			}, {} as QuotationItemsGrouped) || {}
		);
	}
}
